import React, { memo, useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { BookmarkAdd, DeleteOutline, Save } from '@mui/icons-material';
import { clientsAPI } from '../../services/api';

/**
 * Local-state post intent field so keystrokes do not re-render the Gallery page.
 * W3.F3: per-client saved intents dropdown (MRU) + save-to-library.
 */
function GalleryPostIntentField({
  assetId,
  clientId,
  initialValue,
  saving,
  onLocalChange,
  onSave,
  showError,
  showSuccess,
}) {
  const [value, setValue] = useState(initialValue || '');
  const [library, setLibrary] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveBusy, setSaveBusy] = useState(false);

  useEffect(() => {
    setValue(initialValue || '');
    setSelected(null);
  }, [assetId, initialValue]);

  const loadLibrary = useCallback(async () => {
    if (!clientId) {
      setLibrary([]);
      return;
    }
    setLibraryLoading(true);
    try {
      const rows = await clientsAPI.listSavedCaptionIntents(clientId);
      setLibrary(Array.isArray(rows) ? rows : []);
    } catch (e) {
      showError?.(
        e?.response?.data?.detail || e.message || 'Failed to load saved intents',
      );
    } finally {
      setLibraryLoading(false);
    }
  }, [clientId, showError]);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const handleSelectSaved = async (_event, option) => {
    if (!option || !clientId) {
      setSelected(null);
      return;
    }
    setSelected(option);
    setValue(option.intent_text || '');
    onLocalChange?.(option.intent_text || '');
    try {
      const updated = await clientsAPI.useSavedCaptionIntent(clientId, option.id);
      setLibrary((prev) => {
        const rest = prev.filter((r) => r.id !== updated.id);
        return [updated, ...rest];
      });
      setSelected(updated);
    } catch (e) {
      // Prefill already applied; MRU update is best-effort.
      showError?.(
        e?.response?.data?.detail || e.message || 'Could not update intent usage',
      );
    }
  };

  const handleSaveToLibrary = async () => {
    if (!clientId) return;
    const intentText = (value || '').trim();
    if (!intentText) {
      showError?.('Enter intent text before saving to the library');
      return;
    }
    setSaveBusy(true);
    try {
      const created = await clientsAPI.createSavedCaptionIntent(clientId, {
        name: saveName,
        intent_text: intentText,
      });
      setLibrary((prev) => [created, ...prev.filter((r) => r.id !== created.id)]);
      setSelected(created);
      setSaveOpen(false);
      setSaveName('');
      showSuccess?.('Saved to intent library');
    } catch (e) {
      showError?.(
        e?.response?.data?.detail || e.message || 'Failed to save intent to library',
      );
    } finally {
      setSaveBusy(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (!clientId || !selected?.id) return;
    try {
      await clientsAPI.deleteSavedCaptionIntent(clientId, selected.id);
      setLibrary((prev) => prev.filter((r) => r.id !== selected.id));
      setSelected(null);
      showSuccess?.('Removed from intent library');
    } catch (e) {
      showError?.(
        e?.response?.data?.detail || e.message || 'Failed to delete saved intent',
      );
    }
  };

  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ color: '#8b949e' }}>
        Post intent (steers captions)
      </Typography>
      <Typography variant="caption" sx={{ color: '#6e7681' }}>
        Describe what this post is for (e.g. blog promo for tree care, CTA, offer).
        Pick a saved intent for this client, or type freely — still editable before
        generate. Client-wide guardrails live in Client onboarding → AI profile.
      </Typography>

      <Autocomplete
        size="small"
        options={library}
        loading={libraryLoading}
        value={selected}
        onChange={handleSelectSaved}
        getOptionLabel={(opt) => opt?.name || ''}
        isOptionEqualToValue={(a, b) => a?.id === b?.id}
        noOptionsText={
          clientId ? 'No saved intents yet — save one below' : 'Select a client first'
        }
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option.id}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" noWrap>
                {option.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {(option.intent_text || '').slice(0, 80)}
                {(option.intent_text || '').length > 80 ? '…' : ''}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Saved intents"
            placeholder="Select a saved intent"
            InputProps={{
              ...params.InputProps,
              sx: { color: '#f0f6fc', fontSize: '0.875rem' },
              endAdornment: (
                <>
                  {libraryLoading ? (
                    <CircularProgress color="inherit" size={16} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': { bgcolor: '#0d1117' },
              '& .MuiInputLabel-root': { color: '#8b949e' },
            }}
          />
        )}
      />

      <TextField
        fullWidth
        multiline
        minRows={3}
        maxRows={8}
        size="small"
        placeholder="Example: Blog promo — link to spring pruning guide; Signature Tree and Home; no digital-nomad or hustle metaphors."
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setSelected(null);
        }}
        onBlur={() => onLocalChange?.(value)}
        InputProps={{ sx: { color: '#f0f6fc', fontSize: '0.875rem' } }}
        sx={{
          '& .MuiOutlinedInput-root': { bgcolor: '#0d1117' },
          '& .MuiInputLabel-root': { color: '#8b949e' },
        }}
      />

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="outlined"
          startIcon={
            saving ? <CircularProgress size={14} color="inherit" /> : <Save />
          }
          disabled={saving}
          onClick={() => {
            onLocalChange?.(value);
            onSave?.(value);
          }}
        >
          Save post intent
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<BookmarkAdd />}
          disabled={!clientId || !(value || '').trim()}
          onClick={() => {
            setSaveName(selected?.name || '');
            setSaveOpen(true);
          }}
        >
          Save to library
        </Button>
        {selected?.id ? (
          <IconButton
            size="small"
            aria-label="Delete saved intent"
            onClick={handleDeleteSelected}
            sx={{ color: '#8b949e' }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>

      <Dialog open={saveOpen} onClose={() => !saveBusy && setSaveOpen(false)}>
        <DialogTitle>Save intent to library</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Name this intent for {clientId ? 'this client' : 'the client'}. It will
            appear in the Saved intents list (most recently used first).
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="e.g. Before/after tattoo promo"
          />
        </DialogContent>
        <DialogActions>
          <Button disabled={saveBusy} onClick={() => setSaveOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={saveBusy || !(saveName || '').trim()}
            onClick={handleSaveToLibrary}
          >
            {saveBusy ? <CircularProgress size={16} color="inherit" /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default memo(GalleryPostIntentField);
